import { buildClasses } from "../utils/buildClasses";
import { RyeMainContentContainerBase } from "./RyeMainContentContainerBase";

/** How wide a panel's centred column is. */
export type RyeCenteredWidth = "wide" | "narrow";

/** The centred column a panel's top bar and body share with `centerContent`. */
export function ryeCenteredContentClasses(width: RyeCenteredWidth): string {
  return buildClasses("mx-auto", "w-full", {
    switch: width,
    cases: new Map<RyeCenteredWidth, string>([
      ["wide", "max-w-6xl"],
      ["narrow", "max-w-3xl"],
    ]),
  });
}

export function RyeMainContentHeader({
  children,
  title,
  roundedTl = true,
  roundedTr = true,
  centered,
}: {
  children?: React.ReactNode;
  title: string;
  roundedTl?: boolean;
  roundedTr?: boolean;
  /** Lay the title and actions out in the centred column of the body below. */
  centered?: RyeCenteredWidth;
}): JSX.Element {
  const row = (
    <RyeMainContentHeaderRow title={title}>{children}</RyeMainContentHeaderRow>
  );
  return (
    <RyeMainContentContainerBase
      roundedTl={roundedTl}
      roundedTr={roundedTr}
      roundedBl={false}
      roundedBr={false}
      flexGrow={false}
      flexShrink={false}
      height="auto"
      padBottom={false}
      zIndex="20"
      overflowY="visible"
    >
      {centered ? (
        <div className={ryeCenteredContentClasses(centered)}>{row}</div>
      ) : (
        row
      )}
    </RyeMainContentContainerBase>
  );
}

/** The title and actions of a top bar, for surfaces that draw their own bar. */
export function RyeMainContentHeaderRow({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        "w-full",
        "flex",
        "items-center",
        "gap-6",
        "h-11",
        "whitespace-nowrap",
      )}
    >
      <h1
        className={buildClasses(
          "text-base",
          "font-normal",
          "text-on-surface-700",
          "shrink-0",
        )}
      >
        {title}
      </h1>
      {children && (
        <div
          className={buildClasses(
            "flex",
            "flex-1",
            "min-w-0",
            "items-center",
            "gap-3",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
